import { type ThresholdRanges, Validation, type ValidationRule } from "@/libs/cv/validation"
import { normalize } from "@/libs/math"
import {
  createFacePitchAngleDeviationValidation,
  createFaceRollAngleDeviationValidation,
  createFaceYawAngleDeviationValidation,
  createLightingInsufficientValidation,
  createLightningAngleDeviationValidation,
  FACE_DISPLACED,
  FACE_DISTANCE_CLOSE,
  FACE_DISTANCE_FAR,
  type FaceValidationContext,
} from "../validation"
import { STAGE } from "./stages"

export type { ThresholdRanges }

/** Acceptable light intensity */
export const LIGHT_INTENSITY_THRESHOLDS: ThresholdRanges = {
  good: normalize([60, 120], 255 /* normalization of [0, 255] */),
  perfect: normalize([75, 90], 255 /* normalization of [0, 255] */),
}
/** Acceptable light azimuth deviation in [-1, 1] range */
export const LIGHT_AZIMUTH_THRESHOLDS: ThresholdRanges = {
  good: normalize([-18, 18], 90 /* normalization of [-90, 90] */),
  perfect: normalize([-8, 8], 90 /* normalization of [-90, 90] */),
}

/** in deg */
export const SIDE_CAPTURE_FACE_ANGLE = 30
export const PITCH_ANGLE_THRESHOLDS: ThresholdRanges = { good: [-15, 15], perfect: [-10, 10] }
export const YAW_ANGLE_THRESHOLDS: ThresholdRanges = { good: [-15, 15], perfect: [-10, 10] }

/**
 * Dictionary whose keys are the names of the quality problems and values are the lists of the quality validation rules.
 * The dictionary also defines the order in which the quality problems should be displayed to and resolved by user.
 */
const QualityProblems = {
  /**
   * Arises when the face is displaced from the target position, is not fully visible or is out of viewport bounds
   */
  [STAGE.FACE_DISPLACED]: FACE_DISPLACED,
  /** Arises when at least one of the face rotations is deviated from the target one */
  [STAGE.FACE_PITCH_ANGLE_DEVIATION]:
    createFacePitchAngleDeviationValidation(PITCH_ANGLE_THRESHOLDS),
  [STAGE.FACE_YAW_ANGLE_DEVIATION]: createFaceYawAngleDeviationValidation(YAW_ANGLE_THRESHOLDS),
  [STAGE.FACE_ROLL_ANGLE_DEVIATION]: createFaceRollAngleDeviationValidation({
    good: [-15, 15],
    perfect: [-10, 10],
  }),
  /** Arises when the face is too close to camera and should be moved further */
  [STAGE.FACE_DISTANCE_CLOSE]: FACE_DISTANCE_CLOSE,
  /** Arises when the face is too far from camera and should be moved closer */
  [STAGE.FACE_DISTANCE_FAR]: FACE_DISTANCE_FAR,
  /** Arises when Lighting intensity is insufficient */
  [STAGE.LIGHTING_INSUFFICIENT]: createLightingInsufficientValidation({
    good: [LIGHT_INTENSITY_THRESHOLDS.good[0], Infinity],
    perfect: [LIGHT_INTENSITY_THRESHOLDS.perfect[0], Infinity],
  }),
  /** Arises when Lighting intensity is too excessive */
  [STAGE.LIGHTING_EXCESSIVE]: [],
  /** Arises when Lighting direction is deviated from the target one (in front of the face) */
  [STAGE.LIGHTING_ANGLE_DEVIATION]:
    createLightningAngleDeviationValidation(LIGHT_AZIMUTH_THRESHOLDS),
}

const createCheck = <P extends Record<string, ValidationRule<any, any>[]>>(problems: P) => {
  return (ctx: FaceValidationContext): STAGE | null => {
    for (const [name, rules] of Object.entries(problems)) {
      if (ctx.settings?.lighting === 0 && name.startsWith("lighting")) continue

      for (const rule of rules)
        if (!rule.validate(rule.value(ctx) as any, ctx)) return name as STAGE
    }

    return null
  }
}

export const checkFront = createCheck(QualityProblems)
export const checkLeft = createCheck({
  /** Arises when the face is displaced from the target position, is not fully visible or is out of viewport bounds */
  [STAGE.FACE_DISPLACED]: FACE_DISPLACED,
  /** Arises when at least one of the face rotations is deviated from the target one */
  [STAGE.FACE_PITCH_ANGLE_DEVIATION]: QualityProblems[STAGE.FACE_PITCH_ANGLE_DEVIATION],
  [STAGE.FACE_ROLL_ANGLE_DEVIATION]: QualityProblems[STAGE.FACE_ROLL_ANGLE_DEVIATION],
  [STAGE.FACE_DISTANCE_CLOSE]: QualityProblems[STAGE.FACE_DISTANCE_CLOSE],
  [STAGE.FACE_DISTANCE_FAR]: QualityProblems[STAGE.FACE_DISTANCE_FAR],
  [STAGE.ROTATING_LEFT]: [
    Validation<FaceValidationContext, number>({
      /** Face rotation around Z axis in deg */
      value: ({ detections }) => (detections.face ? detections.face.rotation.yaw : NaN),
      validate: (yaw) => yaw <= -SIDE_CAPTURE_FACE_ANGLE,
    }),
  ],
})
export const checkRight = createCheck({
  /**
   * Arises when the face is displaced from the target position, is not fully visible or is out of viewport bounds
   */
  [STAGE.FACE_DISPLACED]: FACE_DISPLACED,
  /** Arises when at least one of the face rotations is deviated from the target one */
  [STAGE.FACE_PITCH_ANGLE_DEVIATION]: QualityProblems[STAGE.FACE_PITCH_ANGLE_DEVIATION],
  [STAGE.FACE_ROLL_ANGLE_DEVIATION]: QualityProblems[STAGE.FACE_ROLL_ANGLE_DEVIATION],
  [STAGE.FACE_DISTANCE_CLOSE]: QualityProblems[STAGE.FACE_DISTANCE_CLOSE],
  [STAGE.FACE_DISTANCE_FAR]: QualityProblems[STAGE.FACE_DISTANCE_FAR],
  [STAGE.ROTATING_RIGHT]: [
    Validation<FaceValidationContext, number>({
      /** Face rotation around Z axis in deg */
      value: ({ detections }) => (detections.face ? detections.face.rotation.yaw : NaN),
      validate: (yaw) => yaw >= SIDE_CAPTURE_FACE_ANGLE,
    }),
  ],
})
